
import { createContext, useContext, useSyncExternalStore, useCallback, type ReactNode } from "react";

export type DocLanguage = "node" | "curl";

interface LanguageContextValue {
  language: DocLanguage;
  setLanguage: (lang: DocLanguage) => void;
}

const LanguageContext = createContext<LanguageContextValue>({
  language: "node",
  setLanguage: () => {},
});

const STORAGE_KEY = "fhirfly-docs-lang";

function getStoredLanguage(): DocLanguage {
  if (typeof window === "undefined") return "node";
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === "curl" ? "curl" : "node";
}

// Simple external store for localStorage-backed language preference
let currentLanguage: DocLanguage = "node";
const listeners = new Set<() => void>();

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function getSnapshot(): DocLanguage {
  return currentLanguage;
}

function getServerSnapshot(): DocLanguage {
  return "node";
}

function setStoredLanguage(lang: DocLanguage) {
  currentLanguage = lang;
  localStorage.setItem(STORAGE_KEY, lang);
  listeners.forEach((cb) => cb());
}

// Initialize from localStorage on first client load
if (typeof window !== "undefined") {
  currentLanguage = getStoredLanguage();
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const language = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const setLanguage = useCallback((lang: DocLanguage) => {
    setStoredLanguage(lang);
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
